/**
 * Phase 18 — a group's lists ranked on the editor's LIVE list formula (POST
 * preview, zero writes): debounced so a slider drag asks once, rows without
 * a metric dropped, the previous answer kept on screen while the next one
 * loads. Asked only while the link's order is FORMULA.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  cleanHourFormula,
  type DropGenListRanking,
  type DropGenListRankingPreview,
} from '@trafficpulse/shared';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/services/api';

import { dropGeneratorKeys } from '../../../../useDropGenerator';

export interface ListRankingArgs {
  countryKey: string;
  campaignWiserId: number | null;
  ranking: DropGenListRanking;
  wiserIds: number[];
  enabled: boolean;
}

export function useListRanking({ countryKey, campaignWiserId, ranking, wiserIds, enabled }: ListRankingArgs) {
  const liveKey = JSON.stringify({ formula: cleanHourFormula(ranking.formula), rules: ranking.rules, wiserIds });
  const bodyKey = useDebouncedValue(liveKey, 400);
  const body = useMemo(() => JSON.parse(bodyKey) as Pick<DropGenListRanking, 'formula' | 'rules'> & { wiserIds: number[] }, [bodyKey]);
  const query = useQuery({
    queryKey: [...dropGeneratorKeys.all, 'list-ranking', countryKey, campaignWiserId, bodyKey] as const,
    queryFn: async () => {
      const res = await api.post<{ data: DropGenListRankingPreview }>(
        `/drop-generator/list-ranking/${countryKey}/${campaignWiserId}/preview`,
        body,
      );
      return res.data.data;
    },
    enabled: enabled && Boolean(countryKey && campaignWiserId) && body.wiserIds.length > 0 && body.formula.groups.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
  return { ...query, pending: liveKey !== bodyKey };
}

export type ListRankingQuery = ReturnType<typeof useListRanking>;
